import { ApolloCache } from "@apollo/client";
import {
    type FieldMergeFunction,
    type FieldReadFunction,
    type KeyArgsFunction,
} from "@apollo/client/cache/inmemory/policies";
import { storeKeyNameFromField } from "@apollo/client/utilities";
import { produce } from "immer";
import { getGroupListV2WithCursor, type GetGroupListV2WithCursorQuery } from "../queries";
import type { FieldNode } from "graphql";

type GroupListResponse = GetGroupListV2WithCursorQuery["getGroupListV2"];
// @ts-ignore
type GroupResponse = GroupListResponse["groupsWithConnections"][number];
type GroupsCache = GroupListResponse & { pages?: { [index: string]: GroupListResponse } };

const ROOT_QUERY_CACHE_KEY = "ROOT_QUERY";
const GROUPS_QUERY_NAME = "getGroupListV2";

export const GROUPS_QUERY_CONNECTION_NAME = "getGroupListV2WithCursor";
export const GROUPS_QUERY_CACHE_KEY = `${GROUPS_QUERY_NAME}:${GROUPS_QUERY_CONNECTION_NAME}`;
export const INITIAL_CURSOR = "";

interface TypePolicy {
    keyArgs: KeyArgsFunction;
    read: FieldReadFunction<GroupsCache, GroupsCache>;
    merge: FieldMergeFunction<GroupsCache, GroupsCache>;
}

/**
 * Cache type policy to handle caching when paginating getGroupListV2 query
 * (used by usePinnedGroups() hook).
 * That is the recommended Apollo Client's approach:
 * https://www.apollographql.com/docs/react/pagination/cursor-based/
 * getGroupListV2 query can be used in old way (offset+limit) and in new way (cursor+limit).
 * We want to keep the old way as is, meaning, cache key is computed based on getGroupListV2's
 * input variables and then complicated useLoadGroupsV2() hook handles pages by itself.
 * The new (recommended) way allows us to define merge() and read() functions
 * in a cache type policy to combine pages and flatten them.
 * We need a way to distinguish these two approaches, therefore, we added @connection directive to
 * "getGroupListV2" query when it is used with cursor.
 */
const groupsPaginationCacheTypePolicy: TypePolicy = {
    keyArgs: (_args, { field, variables }) => {
        // NOTE: if a field has @connection directive, it will be returned here:
        const storeKeyName = storeKeyNameFromField(field as FieldNode, variables);

        if (storeKeyName !== GROUPS_QUERY_CONNECTION_NAME) {
            // in case of non cursor+limit pagination (default) cache key is computed by
            // type name and input variables, for example,
            // getGroupListV2({"input":{"lensLimit":0,"limit":0,"offset":0,"organizationId":null}})
            // so each page is cached separately
            return storeKeyName;
        }

        // in case of cursor+limit pagination out query is maked with
        // @connection(key: "getGroupListV2WithCursor") directive;
        // in this case we want to use custom cache key for query results
        // that is agnostic to query arguments
        // https://www.apollographql.com/docs/react/pagination/key-args/
        return GROUPS_QUERY_CONNECTION_NAME;
    },

    merge(target, source, { storeFieldName, args }) {
        if (storeFieldName !== GROUPS_QUERY_CACHE_KEY) {
            // in case of non cursor+limit pagination (default) "source" always
            // replaces already cached value
            return source;
        }
        // in case of cursor+limit pagination
        // we want to keep in cache all pages requested in "pages" field;
        // here is how pages is structured:
        // {
        //     "": page1Response,
        //     "Cursor1": page2Response,
        //     "Cursor2": page3Response
        // }
        // where a key is cursor that a corresponding page was requested with;
        // note: each page response has "cursor" that points to next page;
        // given that we can traverse "pages" and build a list of all entries
        // (that is done in read() function below)
        return produce(source, (draft) => {
            draft.pages = {
                ...target?.pages,
                [args?.["input"].cursor]: source,
            };
        });
    },

    read(source, { storeFieldName }) {
        if (storeFieldName !== GROUPS_QUERY_CACHE_KEY) {
            // in case of non cursor+limit pagination (default) "source" is always used as is
            return source;
        }

        // in case of cursor+limit pagination
        // we want to build a list of groups by traversing all pages
        if (!source?.pages) {
            // if we have no pages yet - return "source" as is
            return source;
        }

        // build groups list starting with "" cursor until we read whole cursor chain
        // (check merge() above to see how "pages" field is structured)
        const visitedCursors = new Set<string>();
        const groupsWithConnections: GroupResponse[] = [];
        let cursor = INITIAL_CURSOR;
        while (source.pages.hasOwnProperty(cursor) && !visitedCursors.has(cursor)) {
            const page = source.pages[cursor];
            groupsWithConnections.push(...(page?.groupsWithConnections ?? []));
            visitedCursors.add(cursor);
            cursor = page?.cursor as string;
        }
        return {
            ...source,
            groupsWithConnections,
        };
    },
};

/**
 * Clears groups pagination cache.
 * @param cache Apollo cache
 */
function evictGroupsPaginationCache(cache: ApolloCache<{}>) {
    cache.evict({
        id: ROOT_QUERY_CACHE_KEY,
        fieldName: GROUPS_QUERY_CACHE_KEY,
    });
    cache.gc();
}

export const GroupsPagination = {
    query: getGroupListV2WithCursor,
    cache: {
        typePolicy: {
            [GROUPS_QUERY_NAME]: groupsPaginationCacheTypePolicy,
        },
        evict: evictGroupsPaginationCache,
    },
};
